/**
 * Minimal AppProvider — stores theme/config in localStorage.
 * Simplified from the Armada version for the game platform.
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";

export interface AppConfig {
  theme: "dark" | "light" | "system";
}

const defaultConfig: AppConfig = {
  theme: "dark",
};

interface AppContextValue {
  config: AppConfig;
  setConfig: (updates: Partial<AppConfig>) => void;
}

const AppContext = createContext<AppContextValue>({
  config: defaultConfig,
  setConfig: () => {},
});

export function useAppContext() {
  return useContext(AppContext);
}

export function AppProvider({ children, storageKey = "onegroup:config" }: { children: ReactNode; storageKey?: string }) {
  const [config, setConfigState] = useState<AppConfig>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        return { ...defaultConfig, ...JSON.parse(stored) };
      }
    } catch {
      // Use defaults
    }
    return defaultConfig;
  });

  const setConfig = useCallback((updates: Partial<AppConfig>) => {
    setConfigState((prev) => {
      const next = { ...prev, ...updates };
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        // Storage full or unavailable
      }
      return next;
    });
  }, [storageKey]);

  // Apply theme class to document
  useEffect(() => {
    const root = document.documentElement;
    const resolved = config.theme === "system"
      ? (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light")
      : config.theme;
    root.classList.remove("light", "dark");
    root.classList.add(resolved);
  }, [config.theme]);

  return (
    <AppContext.Provider value={{ config, setConfig }}>
      {children}
    </AppContext.Provider>
  );
}